/* Admin VIP Superuser — single-user detail page.
   Route: /admin/vip/users/:userId
   Tabs: Profile · Subscription · Credits · Activity · Devices · Payments
         · Simulators · Certificates · Admin logs

   All mutations (credits, plan, notes, impersonate) call backend endpoints
   and re-fetch the dossier so the UI always shows the source of truth.
*/
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, User, CreditCard, Activity, Smartphone, Receipt,
  GraduationCap, Award, ShieldAlert, Loader2, Mail, Crown,
  Calendar, Edit3, Save, X, Plus, Minus, UserCheck, AlertCircle,
  CheckCircle2, Lock,
} from "lucide-react";
import { apiGet, apiPost } from "../lib/api";
import { stashAdminSessionForImpersonation } from "../components/ImpersonationBanner";

const ADMIN = {
  primary: "#9E1B32", primarySoft: "#FBF1F3", secondary: "#3A2C5A",
  border: "#E5DBC8", muted: "#7a6e57", text: "#1B1235",
  success: "#1A8A53", warn: "#B8862F", danger: "#C8102E", cream: "#FBF7EE",
};

const TAB_DEFS = [
  { id: "profile", label: "Profilo", icon: User },
  { id: "subscription", label: "Sottoscrizione", icon: Crown },
  { id: "credits", label: "Crediti", icon: CreditCard },
  { id: "activity", label: "Attività", icon: Activity },
  { id: "devices", label: "Dispositivi", icon: Smartphone },
  { id: "payments", label: "Pagamenti", icon: Receipt },
  { id: "simulators", label: "Simulatori", icon: GraduationCap },
  { id: "certificates", label: "Certificati", icon: Award },
  { id: "admin_log", label: "Log admin", icon: ShieldAlert },
];

const PLAN_LABELS = {
  free: "Free", premium: "Premium", premium_plus: "Premium+",
  academy_active: "Academy attivo", academy_lifetime: "Academy a vita",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function Badge({ children, kind = "muted" }) {
  const map = {
    success: { bg: "#E6F5EC", color: "#1A8A53" },
    warn: { bg: "#FEF3C7", color: "#92400E" },
    danger: { bg: "#FEE2E2", color: "#991B1B" },
    muted: { bg: "#F1ECE2", color: "#7a6e57" },
    primary: { bg: ADMIN.primarySoft, color: ADMIN.primary },
  };
  const s = map[kind] || map.muted;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: s.bg, color: s.color }}>
      {children}
    </span>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="rounded-xl border bg-white p-5 mb-4" style={{ borderColor: ADMIN.border }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg" style={{ color: ADMIN.text }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ===== Main page =====

export default function VIPAdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/admin/vip/users/${userId}/dossier`);
      setDossier(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento dossier");
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const startImpersonation = async () => {
    if (!window.confirm(
      `Confermi di voler impersonare ${dossier.profile.email}?\n\n` +
      `Verrà creata una sessione temporanea (2h max). ` +
      `Potrai uscire in qualsiasi momento dal banner rosso in alto.`
    )) return;
    try {
      stashAdminSessionForImpersonation();
      await apiPost(`/admin/vip/users/${userId}/impersonate`);
      toast.success("Impersonation attiva");
      // Hard reload to /dashboard so the impersonated user experience is clean.
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation fallita");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: ADMIN.primary }} />
      </div>
    );
  }
  if (error || !dossier) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-8 w-8 mx-auto mb-3" style={{ color: ADMIN.danger }} />
        <p className="text-sm" style={{ color: ADMIN.danger }}>{error || "Dossier non disponibile"}</p>
        <Link to="/admin/vip/subscribers" className="text-sm underline mt-2 inline-block"
              style={{ color: ADMIN.primary }}>Torna alla lista</Link>
      </div>
    );
  }

  const { profile, subscription, credits } = dossier;

  return (
    <div className="space-y-4" data-testid="admin-user-detail">
      {/* Header */}
      <div className="rounded-xl border bg-white p-5" style={{ borderColor: ADMIN.border }}>
        <button onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-xs mb-3"
                style={{ color: ADMIN.muted }}>
          <ArrowLeft className="h-3 w-3" /> Indietro
        </button>
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="font-serif text-2xl" style={{ color: ADMIN.text }}>
                {profile.name || profile.email}
              </h1>
              {subscription.is_admin && <Badge kind="warn">Admin</Badge>}
              {subscription.academy_lifetime && <Badge kind="success">Academy a vita</Badge>}
              {subscription.academy_active && !subscription.academy_lifetime && <Badge kind="success">Academy attivo</Badge>}
              {subscription.is_premium && !subscription.academy_active && <Badge kind="primary">Premium</Badge>}
              {!subscription.is_premium && !subscription.academy_active && !subscription.is_admin && <Badge>Free</Badge>}
            </div>
            <p className="text-sm font-mono" style={{ color: ADMIN.muted }}>{profile.email}</p>
            <p className="text-[11px] font-mono mt-0.5" style={{ color: ADMIN.muted }}>{profile.user_id}</p>
            <p className="text-xs mt-2" style={{ color: ADMIN.muted }}>
              <Calendar className="inline h-3 w-3 mr-1" /> Registrato: {fmt(profile.created_at)}
              {" · "}Ultimo accesso: {fmt(profile.last_session_at)}
              {" · "}Lingua: {(profile.preferred_language || "it").toUpperCase()}
            </p>
          </div>
          {!subscription.is_admin && (
            <button onClick={startImpersonation}
                    data-testid="admin-impersonate-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow"
                    style={{ background: ADMIN.danger }}>
              <UserCheck className="h-4 w-4" /> Impersona utente
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-xl border bg-white" style={{ borderColor: ADMIN.border }}>
        <div className="flex flex-wrap border-b" style={{ borderColor: ADMIN.border }}>
          {TAB_DEFS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
                    data-testid={`tab-${id}`}
                    className="px-4 py-2.5 text-sm font-medium border-b-2 inline-flex items-center gap-1.5 transition-colors"
                    style={{
                      borderColor: tab === id ? ADMIN.primary : "transparent",
                      color: tab === id ? ADMIN.primary : ADMIN.muted,
                      background: tab === id ? ADMIN.primarySoft : "white",
                    }}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === "profile" && <ProfileTab dossier={dossier} userId={userId} onReload={load} />}
          {tab === "subscription" && <SubscriptionTab dossier={dossier} userId={userId} onReload={load} />}
          {tab === "credits" && <CreditsTab dossier={dossier} userId={userId} onReload={load} />}
          {tab === "activity" && <ActivityTab dossier={dossier} />}
          {tab === "devices" && <DevicesTab dossier={dossier} />}
          {tab === "payments" && <PaymentsTab dossier={dossier} />}
          {tab === "simulators" && <SimulatorsTab dossier={dossier} />}
          {tab === "certificates" && <CertificatesTab dossier={dossier} />}
          {tab === "admin_log" && <AdminLogTab dossier={dossier} />}
        </div>
      </div>
    </div>
  );
}

// ===== Tab: Profile (+ admin notes) =====

function ProfileTab({ dossier, userId, onReload }) {
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = dossier.notes || [];

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await apiPost(`/admin/vip/users/${userId}/notes`, { note: noteText.trim() });
      setNoteText("");
      toast.success("Nota aggiunta");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Note interne admin">
        <div className="flex gap-2 mb-3">
          <input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && addNote()}
                 placeholder="es: regalo influencer, beta tester, problema Stripe risolto manualmente…"
                 data-testid="admin-note-input"
                 className="flex-1 border rounded-lg px-3 py-2 text-sm"
                 style={{ borderColor: ADMIN.border, color: ADMIN.text }} />
          <button onClick={addNote} disabled={saving || !noteText.trim()}
                  data-testid="admin-note-save-btn"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                  style={{ background: ADMIN.primary }}>
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </button>
        </div>
        {notes.length === 0 ? (
          <p className="text-sm italic" style={{ color: ADMIN.muted }}>
            Nessuna nota interna ancora.
          </p>
        ) : (
          <div className="space-y-2" data-testid="admin-notes-list">
            {notes.map((n) => (
              <div key={n.note_id}
                   className="p-3 rounded-lg border text-sm"
                   style={{ borderColor: ADMIN.border, background: ADMIN.cream }}>
                <p style={{ color: ADMIN.text }}>{n.text}</p>
                <p className="text-[11px] mt-1" style={{ color: ADMIN.muted }}>
                  {fmt(n.created_at)} · da {n.admin_email}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Email & comunicazioni">
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div><span style={{ color: ADMIN.muted }}>Email:</span>{" "}
            <span className="font-mono">{dossier.profile.email}</span></div>
          <div><span style={{ color: ADMIN.muted }}>Disiscritta:</span>{" "}
            {dossier.profile.email_unsubscribed_at
              ? <Badge kind="danger">Sì, da {fmt(dossier.profile.email_unsubscribed_at)}</Badge>
              : <Badge kind="success">Attiva</Badge>}
          </div>
        </div>
      </Section>
    </div>
  );
}

// ===== Tab: Subscription (plan override) =====

function SubscriptionTab({ dossier, userId, onReload }) {
  const sub = dossier.subscription;
  const validPlans = dossier.valid_plans || [];
  const [selectedPlan, setSelectedPlan] = useState("");
  const [days, setDays] = useState(365);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!selectedPlan) { toast.error("Seleziona un piano"); return; }
    if (!window.confirm(
      `Confermi override del piano a "${PLAN_LABELS[selectedPlan]}"` +
      (["premium", "premium_plus", "academy_active"].includes(selectedPlan)
        ? ` per ${days} giorni?` : "?")
    )) return;
    setSaving(true);
    try {
      await apiPost(`/admin/vip/users/${userId}/plan`,
                    { plan: selectedPlan, days: parseInt(days, 10), reason });
      toast.success("Piano aggiornato");
      setSelectedPlan(""); setReason("");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Stato attuale">
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Piano" value={sub.subscription_level || "free"} />
          <Field label="Premium?" value={sub.is_premium ? "Sì" : "No"} />
          <Field label="Premium until" value={fmt(sub.premium_until)} />
          <Field label="Academy attivo" value={sub.academy_active ? "Sì" : "No"} />
          <Field label="Academy lifetime" value={sub.academy_lifetime ? "Sì" : "No"} />
          <Field label="Academy expires" value={fmt(sub.academy_expires_at)} />
          <Field label="Stripe customer" value={sub.stripe_customer_id || "—"} mono />
          <Field label="Stripe sub status" value={sub.stripe_subscription_status || "—"} />
        </div>
      </Section>

      <Section title="Override manuale del piano">
        <p className="text-xs mb-3" style={{ color: ADMIN.muted }}>
          ⚠️ Questa azione NON crea un pagamento Stripe. Usare solo per regali,
          rimborsi manuali, ambasciatori, beta tester. Ogni cambio è registrato nel log admin.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <select value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}
                  data-testid="admin-plan-select"
                  className="border rounded-lg px-3 py-2 text-sm"
                  style={{ borderColor: ADMIN.border }}>
            <option value="">— Seleziona piano —</option>
            {validPlans.map((p) => (
              <option key={p} value={p}>{PLAN_LABELS[p] || p}</option>
            ))}
          </select>
          <input type="number" min="1" max="3650" value={days}
                 onChange={(e) => setDays(e.target.value)}
                 disabled={["free", "academy_lifetime"].includes(selectedPlan)}
                 data-testid="admin-plan-days"
                 placeholder="Giorni"
                 className="border rounded-lg px-3 py-2 text-sm"
                 style={{ borderColor: ADMIN.border }} />
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Motivo (es: regalo influencer)"
                 data-testid="admin-plan-reason"
                 className="border rounded-lg px-3 py-2 text-sm"
                 style={{ borderColor: ADMIN.border }} />
        </div>
        <button onClick={apply} disabled={saving || !selectedPlan}
                data-testid="admin-plan-apply-btn"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ background: ADMIN.primary }}>
          <Save className="h-3.5 w-3.5" /> {saving ? "Salvataggio…" : "Applica piano"}
        </button>
      </Section>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider mb-0.5"
         style={{ color: ADMIN.muted }}>{label}</p>
      <p className={mono ? "font-mono text-xs" : "text-sm"} style={{ color: ADMIN.text }}>
        {value || "—"}
      </p>
    </div>
  );
}

// ===== Tab: Credits =====

function CreditsTab({ dossier, userId, onReload }) {
  const credits = dossier.credits;
  const types = dossier.credit_types || {};
  const [selectedType, setSelectedType] = useState("");
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!selectedType) { toast.error("Seleziona un tipo di credito"); return; }
    setSaving(true);
    try {
      await apiPost(`/admin/vip/users/${userId}/credits`,
                    { credit_type: selectedType, delta: parseInt(delta, 10), reason });
      toast.success("Crediti aggiornati");
      setSelectedType(""); setDelta(1); setReason("");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const counterRows = [
    { type: "exam_paid", label: "Esame scritto · paid", value: credits.exam_paid_remaining },
    { type: "exam_free", label: "Esame scritto · free", value: 1 - credits.exam_free_used,
      max: 1, used: credits.exam_free_used },
    { type: "oral_paid", label: "Orale 5-slots · paid", value: credits.oral_paid_remaining },
    { type: "oral_coach_paid", label: "Coach Orale · paid", value: credits.oral_coach_paid_remaining },
    { type: "oral_coach_free", label: "Coach Orale · free", value: 1 - credits.oral_coach_free_used,
      max: 1, used: credits.oral_coach_free_used },
  ];

  return (
    <div className="space-y-4">
      <Section title="Stato crediti">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-credits-summary">
          {counterRows.map((r) => (
            <div key={r.type} className="rounded-lg p-3 border"
                 style={{ borderColor: ADMIN.border, background: ADMIN.cream }}>
              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: ADMIN.muted }}>
                {r.label}
              </p>
              <p className="font-serif text-2xl" style={{ color: ADMIN.primary }}>
                {r.value}{r.max ? ` / ${r.max}` : ""}
              </p>
              {r.used !== undefined && (
                <p className="text-[10px]" style={{ color: ADMIN.muted }}>
                  {r.used} usati
                </p>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Aggiungi / sottrai crediti">
        <p className="text-xs mb-3" style={{ color: ADMIN.muted }}>
          Per i contatori "free": delta positivo ripristina il tentativo gratuito.
          Per "days" (Premium / Academy): estende o accorcia la scadenza.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}
                  data-testid="admin-credit-type"
                  className="border rounded-lg px-3 py-2 text-sm"
                  style={{ borderColor: ADMIN.border }}>
            <option value="">— Tipo di credito —</option>
            {Object.entries(types).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <button onClick={() => setDelta(Math.max(-999, parseInt(delta, 10) - 1))}
                    className="h-9 w-9 rounded-lg border grid place-items-center"
                    style={{ borderColor: ADMIN.border, color: ADMIN.muted }}>
              <Minus className="h-4 w-4" />
            </button>
            <input type="number" value={delta}
                   onChange={(e) => setDelta(e.target.value)}
                   data-testid="admin-credit-delta"
                   className="w-full border rounded-lg px-3 py-2 text-sm text-center"
                   style={{ borderColor: ADMIN.border }} />
            <button onClick={() => setDelta(parseInt(delta, 10) + 1)}
                    className="h-9 w-9 rounded-lg border grid place-items-center"
                    style={{ borderColor: ADMIN.border, color: ADMIN.muted }}>
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Motivo (es: rimborso Stripe)"
                 data-testid="admin-credit-reason"
                 className="border rounded-lg px-3 py-2 text-sm"
                 style={{ borderColor: ADMIN.border }} />
        </div>
        <button onClick={apply} disabled={saving || !selectedType}
                data-testid="admin-credit-apply-btn"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ background: ADMIN.primary }}>
          <Save className="h-3.5 w-3.5" /> {saving ? "Salvataggio…" : "Applica"}
        </button>
      </Section>
    </div>
  );
}

// ===== Tab: Activity (timeline) =====

function ActivityTab({ dossier }) {
  // Merge written + oral + coach attempts into a single chronological feed.
  const events = [];
  (dossier.written_attempts || []).forEach((a) => events.push({
    when: a.submitted_at || a.started_at, kind: "Esame scritto",
    sub: `${a.mode || "real"} · ${a.passed ? "Passato" : "Non passato"}`,
    badge: a.mode === "real" ? "primary" : "muted",
  }));
  (dossier.coach_attempts || []).forEach((a) => events.push({
    when: a.submitted_at || a.started_at, kind: "Coach Orale",
    sub: `${a.qid} · ${a.mode}${a.evaluation?.score != null ? ` · ${a.evaluation.score}/10` : ""}`,
    badge: a.mode === "paid" ? "primary" : "muted",
  }));
  (dossier.oral_attempts || []).forEach((a) => events.push({
    when: a.submitted_at || a.started_at, kind: "Simulatore Orale",
    sub: `${a.status} · ${a.difficulty || "?"}`,
    badge: "muted",
  }));
  events.sort((a, b) => (b.when || "").localeCompare(a.when || ""));

  if (!events.length) {
    return <p className="text-sm italic" style={{ color: ADMIN.muted }}>
      Nessuna attività ancora registrata.
    </p>;
  }
  return (
    <div className="space-y-2" data-testid="admin-activity-list">
      {events.map((e, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg border"
             style={{ borderColor: ADMIN.border }}>
          <div className="text-[10px] mt-1 shrink-0" style={{ color: ADMIN.muted }}>
            {fmt(e.when)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium" style={{ color: ADMIN.text }}>{e.kind}</p>
            <p className="text-xs" style={{ color: ADMIN.muted }}>{e.sub}</p>
          </div>
          <Badge kind={e.badge}>{e.kind.split(" ")[0]}</Badge>
        </div>
      ))}
    </div>
  );
}

function DevicesTab({ dossier }) {
  const devices = dossier.devices || [];
  if (!devices.length) {
    return <p className="text-sm italic" style={{ color: ADMIN.muted }}>
      Nessun dispositivo registrato.
    </p>;
  }
  return (
    <div className="space-y-2" data-testid="admin-devices-list">
      {devices.map((d) => (
        <div key={d.fingerprint_id} className="p-3 rounded-lg border"
             style={{ borderColor: ADMIN.border }}>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-mono text-xs">{d.fingerprint_id}</span>
            {d.suspicious_flag && <Badge kind="danger">Sospetto</Badge>}
            {d.free_exam_consumed && <Badge kind="warn">Free consumato</Badge>}
            {d.unlocked_by_admin && <Badge kind="success">Sbloccato</Badge>}
          </div>
          <p className="text-[11px]" style={{ color: ADMIN.muted }}>
            {d.components_sample?.timezone} · {d.components_sample?.screen} · {d.last_ip_prefix || "ip ?"} · {(d.user_ids || []).length} account
          </p>
          <p className="text-[11px]" style={{ color: ADMIN.muted }}>
            Ultimo accesso: {fmt(d.last_seen_at)}
          </p>
        </div>
      ))}
    </div>
  );
}

function PaymentsTab({ dossier }) {
  const payments = dossier.payments || [];
  if (!payments.length) {
    return <p className="text-sm italic" style={{ color: ADMIN.muted }}>
      Nessun pagamento registrato in `stripe_events` per questo utente.
    </p>;
  }
  return (
    <div className="space-y-2" data-testid="admin-payments-list">
      {payments.map((p) => (
        <div key={p.event_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-sm"
             style={{ borderColor: ADMIN.border }}>
          <span className="font-mono text-[11px]" style={{ color: ADMIN.muted }}>{p.event_id}</span>
          <Badge kind="primary">{p.type}</Badge>
          {p.plan_id && <Badge>{p.plan_id}</Badge>}
          {p.amount_total != null && (
            <span className="ml-auto font-semibold" style={{ color: ADMIN.success }}>
              €{(p.amount_total / 100).toFixed(2)}
            </span>
          )}
          <span className="text-[11px]" style={{ color: ADMIN.muted }}>{fmt(p.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

function SimulatorsTab({ dossier }) {
  const written = dossier.written_attempts || [];
  const oral = dossier.oral_attempts || [];
  const coach = dossier.coach_attempts || [];
  return (
    <div className="space-y-4">
      <Section title={`Esame scritto (${written.length})`}>
        {written.length === 0 ? (
          <p className="text-sm italic" style={{ color: ADMIN.muted }}>Nessun tentativo.</p>
        ) : written.map((a) => (
          <div key={a.attempt_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-sm mb-1"
               style={{ borderColor: ADMIN.border }}>
            <span className="font-mono text-[11px]">{a.attempt_id}</span>
            <Badge kind={a.mode === "real" ? "primary" : "muted"}>{a.mode || "real"}</Badge>
            <Badge kind={a.passed ? "success" : "danger"}>{a.passed ? "Passato" : "Non passato"}</Badge>
            {a.certificate_id && <Badge kind="warn">Cert</Badge>}
            <span className="ml-auto text-[11px]" style={{ color: ADMIN.muted }}>
              {fmt(a.submitted_at || a.started_at)}
            </span>
          </div>
        ))}
      </Section>
      <Section title={`Simulatore Orale (${oral.length})`}>
        {oral.length === 0 ? (
          <p className="text-sm italic" style={{ color: ADMIN.muted }}>Nessun tentativo.</p>
        ) : oral.map((a) => (
          <div key={a.attempt_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-sm mb-1"
               style={{ borderColor: ADMIN.border }}>
            <span className="font-mono text-[11px]">{a.attempt_id}</span>
            <Badge>{a.status}</Badge>
            <span className="ml-auto text-[11px]" style={{ color: ADMIN.muted }}>
              {fmt(a.submitted_at || a.started_at)}
            </span>
          </div>
        ))}
      </Section>
      <Section title={`Coach Orale IA (${coach.length})`}>
        {coach.length === 0 ? (
          <p className="text-sm italic" style={{ color: ADMIN.muted }}>Nessun tentativo.</p>
        ) : coach.map((a) => (
          <div key={a.attempt_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-sm mb-1"
               style={{ borderColor: ADMIN.border }}>
            <span className="font-mono text-[11px]">{a.qid}</span>
            <Badge kind={a.mode === "paid" ? "primary" : "muted"}>{a.mode}</Badge>
            {a.evaluation?.score != null && <Badge kind="warn">{a.evaluation.score}/10</Badge>}
            <span className="ml-auto text-[11px]" style={{ color: ADMIN.muted }}>
              {fmt(a.submitted_at || a.started_at)}
            </span>
          </div>
        ))}
      </Section>
    </div>
  );
}

function CertificatesTab({ dossier }) {
  const certs = dossier.certificates || [];
  if (!certs.length) {
    return <p className="text-sm italic" style={{ color: ADMIN.muted }}>
      Nessun certificato emesso.
    </p>;
  }
  return (
    <div className="space-y-2" data-testid="admin-certs-list">
      {certs.map((c) => (
        <div key={c.certificate_id} className="p-3 rounded-lg border"
             style={{ borderColor: ADMIN.border }}>
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4" style={{ color: ADMIN.warn }} />
            <span className="font-mono text-xs">{c.certificate_id}</span>
            <span className="ml-auto text-[11px]" style={{ color: ADMIN.muted }}>
              {fmt(c.issued_at)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminLogTab({ dossier }) {
  const log = dossier.admin_log || [];
  if (!log.length) {
    return <p className="text-sm italic" style={{ color: ADMIN.muted }}>
      Nessuna azione admin registrata su questo utente.
    </p>;
  }
  return (
    <div className="space-y-2" data-testid="admin-log-list">
      {log.map((a) => (
        <div key={a.action_id} className="p-3 rounded-lg border"
             style={{ borderColor: ADMIN.border }}>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge kind="primary">{a.action_type}</Badge>
            <span className="text-xs" style={{ color: ADMIN.muted }}>
              {fmt(a.created_at)}
            </span>
            <span className="ml-auto font-mono text-[11px]" style={{ color: ADMIN.muted }}>
              {a.admin_email}
            </span>
          </div>
          <pre className="text-[11px] font-mono whitespace-pre-wrap p-2 rounded"
               style={{ background: ADMIN.cream, color: ADMIN.text }}>
            {JSON.stringify(a.payload || {}, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}
